import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X, LogIn, LogOut, ShieldCheck, User, HeartHandshake, UserCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { resolveMenu, pruneMenu } from '../lib/menu';
import { ADMIN_SECTIONS } from '../lib/adminSections';
import SearchBar from './SearchBar';
import GroupSiteActions from './GroupSiteActions';

export default function Navbar() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, currentClass, settings, customPages, canAccess, isAdmin, startPreview, classLabel, memberClasses } = useAuth();

  const isActive = (path) => location.pathname === path;
  const hiddenNav = (settings && settings.layout && settings.layout.hiddenNav) || [];

  // Resolve the persisted menu tree (order / parenting / visibility), then apply
  // role-based access + hidden + draft filters so only accessible items appear per role.
  const menuTree = (settings && settings.menuTree) || [];
  const customGroups = (settings && settings.customGroups) || [];
  const resolved = resolveMenu({ customPages, menuTree, customGroups });
  const nowMs = Date.now();
  const isLive = (c) => !c.draft || (c.publishAt && new Date(c.publishAt).getTime() <= nowMs);
  const showPage = (c) => !c.hidden && canAccess(c.pageKey) && (isLive(c) || isAdmin);
  const adminSections = ADMIN_SECTIONS.filter((s) => canAccess(s.perm));
  const [heldEmails, setHeldEmails] = useState(0);
  const canSeeQueue = !!user && canAccess('admin-email');
  useEffect(() => {
    if (!canSeeQueue) { setHeldEmails(0); return undefined; }
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-queue/count`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (d) setHeldEmails(d.held || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    const onCount = (e) => setHeldEmails(Number(e.detail) || 0);
    window.addEventListener('email-queue-count', onCount);
    return () => { clearInterval(t); window.removeEventListener('email-queue-count', onCount); };
  }, [canSeeQueue, location.pathname]);
  // Recursively prune hidden / inaccessible pages; drop groups left with no visible content.
  const visibleNav = pruneMenu(resolved.filter((n) => !hiddenNav.includes(n.label)), showPage);

  const navLinkStyle = (active) => ({
    color: active ? '#ffd97a' : 'var(--cream)',
  });

  // Desktop dropdown list; nested groups open as fly-outs to the right on hover.
  const renderDesktopChildren = (children, depth) => (
    <ul
      className={`nav-dropdown absolute min-w-[230px] py-1 shadow-xl z-50 ${depth === 0 ? 'left-0 top-full' : 'left-full top-0'}`}
      style={{ background: 'var(--navy-dark)' }}
      data-testid={depth === 0 ? undefined : 'nav-flyout'}
    >
      {children.map((child) => (
        <li key={child.id} className="relative group/sub" data-testid={`nav-item-${child.id}`}>
          {child.kind === 'group' ? (
            <>
              {child.landingPath ? (
                <Link to={child.landingPath} data-testid={`nav-group-link-${child.id}`} className="flex items-center justify-between px-4 py-2.5 text-[14px] transition-colors hover:bg-[var(--maroon)]" style={{ color: 'var(--cream)' }}>
                  {child.label} <ChevronRight size={14} />
                </Link>
              ) : (
                <div className="flex items-center justify-between px-4 py-2.5 text-[14px] transition-colors hover:bg-[var(--maroon)] cursor-default" style={{ color: 'var(--cream)' }}>
                  {child.label} <ChevronRight size={14} />
                </div>
              )}
              {child.children.length > 0 && (
                <div className="hidden group-hover/sub:block">
                  {renderDesktopChildren(child.children, depth + 1)}
                </div>
              )}
            </>
          ) : (
            <Link
              to={child.path}
              className="block px-4 py-2.5 text-[14px] transition-colors hover:bg-[var(--maroon)]"
              style={{ color: 'var(--cream)' }}
            >
              {child.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  // Mobile: nested collapsible sections.
  const renderMobileChildren = (children) => (
    <ul className="pl-4 pb-1">
      {children.map((c) => (
        <li key={c.id}>
          {c.kind === 'group' ? (
            <details data-testid={`nav-mobile-group-${c.id}`}>
              <summary className="px-2 py-2 text-[14px] cursor-pointer flex items-center justify-between">
                {c.landingPath ? <Link to={c.landingPath} data-testid={`nav-mobile-group-link-${c.id}`} onClick={() => setMobileOpen(false)}>{c.label}</Link> : c.label}
                <ChevronDown size={14} />
              </summary>
              {renderMobileChildren(c.children)}
            </details>
          ) : (
            <Link to={c.path} className="block px-2 py-2 text-[14px]" onClick={() => setMobileOpen(false)}>
              {c.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <nav style={{ background: 'var(--navy)' }} className="shadow-md sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between gap-2 min-h-12 flex-wrap" data-testid="navbar-row">
          {/* Desktop menu (wraps onto extra rows rather than overflowing the screen) */}
          <ul className="hidden xl:flex flex-wrap items-center grow min-w-0 font-title text-[14px]" data-testid="desktop-nav">
            {visibleNav.map((item) => (
              <li
                key={item.label}
                className="relative h-12"
                onMouseEnter={() => item.children && setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
              >
                {item.children ? (
                  item.landingPath ? (
                    <Link
                      to={item.landingPath}
                      data-testid={`nav-group-link-${item.id}`}
                      className="flex items-center gap-1 h-full px-3 transition-colors hover:bg-[var(--navy-dark)] whitespace-nowrap"
                      style={navLinkStyle(isActive(item.landingPath))}
                    >
                      {item.label}
                      <ChevronDown size={14} />
                    </Link>
                  ) : (
                    <button
                      className="flex items-center gap-1 h-full px-3 transition-colors hover:bg-[var(--navy-dark)] whitespace-nowrap"
                      style={navLinkStyle(false)}
                    >
                      {item.label}
                      <ChevronDown size={14} />
                    </button>
                  )
                ) : (
                  <Link
                    to={item.path}
                    className="flex items-center h-full px-3 transition-colors hover:bg-[var(--navy-dark)] whitespace-nowrap"
                    style={navLinkStyle(isActive(item.path))}
                  >
                    {item.label}
                  </Link>
                )}

                {item.children && openMenu === item.label && item.children.length > 0 && renderDesktopChildren(item.children, 0)}
              </li>
            ))}
          </ul>

          {/* Mobile toggle */}
          <button
            className="xl:hidden p-2"
            style={{ color: 'var(--cream)' }}
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          {/* Auth area */}
          <div className="flex items-center gap-3 font-title text-[14px] shrink-0 ml-auto h-12">
            <SearchBar />
            <Link to="/support" className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded transition-colors" style={{ background: 'var(--gold)', color: '#3a2c06' }}>
              <HeartHandshake size={15} /> Support Us
            </Link>
            {user ? (
              <>
                <Link to="/my-details" className="hidden md:flex items-center gap-1 hover:underline text-sm" style={{ color: 'var(--cream)' }} data-testid="nav-my-details">My Details</Link>
                <Link to="/account" className="hidden md:flex items-center gap-1 hover:underline" style={{ color: 'var(--cream)' }}>
                  <UserCircle size={15} /> {user.name}
                  <span className="px-2 py-0.5 rounded text-[11px]" style={{ background: 'var(--gold)', color: '#3a2c06' }}>
                    {classLabel(user.memberClass)}
                  </span>
                </Link>
                {isAdmin && (
                  <select
                    data-testid="view-as-select"
                    value=""
                    onChange={(e) => { if (e.target.value) { startPreview(e.target.value); e.target.value = ''; } }}
                    title="Open a preview of the site as a member class"
                    className="hidden md:block text-[12px] rounded px-1.5 py-1"
                    style={{ background: 'var(--navy-dark)', color: 'var(--cream)', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    <option value="">View as…</option>
                    {memberClasses.filter((mc) => mc.key !== 'admin').map((mc) => (
                      <option key={mc.key} value={mc.key}>{mc.label}</option>
                    ))}
                  </select>
                )}
                {user && ['group-admin', 'site-admin', 'admin'].includes(user.memberClass) && (
                  <span className="hidden lg:inline-flex"><GroupSiteActions showRequest={false} compact /></span>
                )}
                {adminSections.length > 0 && (
                  <div className="relative">
                    <button
                      data-testid="admin-menu-trigger"
                      onClick={() => setAdminOpen((o) => !o)}
                      className="flex items-center gap-1 hover:underline"
                      style={{ color: '#ffd97a' }}
                    >
                      <ShieldCheck size={15} /> Admin{heldEmails > 0 && <span data-testid="nav-email-queue-badge" className="ml-1 px-1.5 rounded-full text-[11px] font-title" style={{ background: 'var(--gold)', color: '#3a2c06' }}>{heldEmails}</span>} <ChevronDown size={13} />
                    </button>
                    {adminOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setAdminOpen(false)} />
                        <ul className="nav-dropdown absolute right-0 top-full min-w-[230px] py-1 shadow-xl z-50" style={{ background: 'var(--navy-dark)' }}>
                          <li>
                            <Link to="/admin" data-testid="admin-menu-dashboard" onClick={() => setAdminOpen(false)}
                              className="block px-4 py-2 text-[13px] border-b transition-colors hover:bg-[var(--maroon)]"
                              style={{ color: 'var(--cream)', borderColor: 'rgba(255,255,255,0.12)', opacity: 0.85 }}>
                              Dashboard
                            </Link>
                          </li>
                          {adminSections.map((s) => (
                            <li key={s.id}>
                              <Link to={`/admin/${s.id}`} data-testid={`admin-menu-${s.id}`} onClick={() => setAdminOpen(false)}
                                className="flex items-center gap-2 px-4 py-2.5 text-[14px] transition-colors hover:bg-[var(--maroon)]"
                                style={{ color: 'var(--cream)' }}>
                                <s.icon size={14} /> {s.label}{s.id === 'email-queue' && heldEmails > 0 && <span data-testid="admin-menu-email-queue-badge" className="ml-auto px-1.5 rounded-full text-[11px] font-title" style={{ background: 'var(--gold)', color: '#3a2c06' }}>{heldEmails}</span>}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                  style={{ background: 'var(--maroon)', color: 'var(--cream)' }}
                >
                  <LogOut size={15} /> <span className="hidden sm:inline">Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                style={{ background: 'var(--gold)', color: '#3a2c06' }}
              >
                <LogIn size={15} /> Member Login
              </Link>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <ul className="xl:hidden pb-3 font-title" style={{ color: 'var(--cream)' }}>
            {isAdmin && (
              <li className="border-t px-2 py-2.5" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                <select
                  data-testid="view-as-select-mobile"
                  value=""
                  onChange={(e) => { if (e.target.value) { startPreview(e.target.value); e.target.value = ''; setMobileOpen(false); } }}
                  className="w-full text-[14px] rounded px-2 py-2"
                  style={{ background: 'var(--navy-dark)', color: 'var(--cream)', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  <option value="">View site as a member…</option>
                  {memberClasses.filter((mc) => mc.key !== 'admin').map((mc) => (
                    <option key={mc.key} value={mc.key}>{mc.label}</option>
                  ))}
                </select>
              </li>
            )}
            {adminSections.length > 0 && (
              <li className="border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                <details>
                  <summary className="px-2 py-2.5 cursor-pointer flex items-center justify-between" style={{ color: '#ffd97a' }}>
                    <span className="flex items-center gap-1"><ShieldCheck size={15} /> Admin</span> <ChevronDown size={14} />
                  </summary>
                  <ul className="pl-4 pb-1">
                    <li><Link to="/admin" className="block px-2 py-2 text-[14px]" onClick={() => setMobileOpen(false)}>Dashboard</Link></li>
                    {adminSections.map((s) => (
                      <li key={s.id}><Link to={`/admin/${s.id}`} data-testid={`admin-menu-mobile-${s.id}`} className="block px-2 py-2 text-[14px]" onClick={() => setMobileOpen(false)}>{s.label}</Link></li>
                    ))}
                  </ul>
                </details>
              </li>
            )}
            {visibleNav.map((item) => (
              <li key={item.label} className="border-t" style={{ borderColor: 'rgba(255,255,255,0.12)' }}>
                {item.children ? (
                  <details>
                    <summary className="px-2 py-2.5 cursor-pointer flex items-center justify-between">
                      {item.landingPath ? <Link to={item.landingPath} data-testid={`nav-mobile-group-link-${item.id}`} onClick={() => setMobileOpen(false)}>{item.label}</Link> : item.label}
                      <ChevronDown size={14} />
                    </summary>
                    {renderMobileChildren(item.children)}
                  </details>
                ) : (
                  <Link to={item.path} className="block px-2 py-2.5" onClick={() => setMobileOpen(false)}>
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </nav>
  );
}
